/**
 * Gallery Grid — Judge.me review JSON hydration + skeleton fill.
 *
 * Reads review data from window.jdgm.data.allReviewsWidgetV2025.all_reviews,
 * a JSON blob populated by the visible JM all_reviews_page widget on this
 * page. Falls back to parsing the inline <script class="jdgm-all-reviews-
 * widget-v2025-data"> textContent if the global isn't populated yet.
 *
 * JSON fields currently consumed: body, reviewer_name, rating,
 * verified_buyer.
 *
 * JSON fields available but NOT consumed (future feature surface — add
 * to fillSkeleton() if/when cards expand): body_html, pictures_urls,
 * media_platform_hosted_video_infos, created_at, transparency_badges,
 * location, reviewer_initial, title.
 *
 * Failure modes handled:
 * - JSON blob missing or malformed   → hideAll()
 * - Fewer reviews than skeletons     → unfilled skeletons fade
 * - Per-record body missing          → skip record, fade that skeleton
 *
 * Inline-script-tag fallback pattern mirrors assets/rs-gallery-stats.js
 * (commit a5737fd) which reads the same blob for the stats strip.
 */

(function () {
  'use strict';

  const SECTION_SEL = '.rs-gg';
  const SKELETON_SEL = '.rs-gg__card--review.is-loading';
  const QUOTE_ATTR = '[data-rs-gg-quote]';
  const STARS_ATTR = '[data-rs-gg-stars]';
  const NAME_ATTR = '[data-rs-gg-name]';
  const META_ATTR = '[data-rs-gg-meta]';
  const SCRIPT_TAG_SEL = '.jdgm-all-reviews-widget-v2025-data';

  // ── Read JM aggregate data: global object, fallback to inline parse ──
  function readJMData() {
    if (window.jdgm && window.jdgm.data && window.jdgm.data.allReviewsWidgetV2025) {
      return window.jdgm.data.allReviewsWidgetV2025;
    }
    const scriptEl = document.querySelector(SCRIPT_TAG_SEL);
    if (!scriptEl || !scriptEl.textContent) return null;
    const re = /allReviewsWidgetV2025\s*=\s*(\{[\s\S]+?\});/;
    const match = scriptEl.textContent.match(re);
    if (!match) return null;
    try {
      return JSON.parse(match[1]);
    } catch (e) {
      return null;
    }
  }

  // ── Star glyph render (C2 — JudgemeStar U+E000 via <i class="...jm-..."> ─
  function starHTML(filled) {
    const cls = filled ? 'rs-gg__jm-star' : 'rs-gg__jm-star rs-gg__jm-star--empty';
    return '<i class="' + cls + '" aria-hidden="true"></i>';
  }

  // ── HTML entity decode (JM payload entity-encodes product_title) ─────
  function decodeHtmlEntities(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    const t = document.createElement('textarea');
    t.innerHTML = s;
    return t.value;
  }

  // ── One JSON record → fillSkeleton's expected data shape ─────────────
  function recordToData(review) {
    const rating = parseInt(review.rating, 10);
    return {
      stars: isNaN(rating) ? 5 : Math.max(0, Math.min(5, rating)),
      body: typeof review.body === 'string' ? review.body.trim() : '',
      author: typeof review.reviewer_name === 'string' ? review.reviewer_name.trim() : '',
      verified: !!review.verified_buyer,
      productTitle: decodeHtmlEntities(typeof review.product_title === 'string' ? review.product_title.trim() : ''),
      productUrl: typeof review.product_url === 'string' ? review.product_url.trim() : '',
      isShopReview: !!review.is_shop_review,
    };
  }

  // ── Fill one skeleton with real content ──────────────────────────────
  function fillSkeleton(skel, data) {
    if (!data.body) return false;

    const starsEl = skel.querySelector(STARS_ATTR);
    if (starsEl) {
      let html = '';
      for (let i = 0; i < 5; i++) html += starHTML(i < data.stars);
      starsEl.innerHTML = html;
      starsEl.setAttribute('aria-label', data.stars + ' out of 5 stars');
      starsEl.removeAttribute('aria-hidden');
    }

    const quoteEl = skel.querySelector(QUOTE_ATTR);
    if (quoteEl) {
      const p = document.createElement('p');
      p.className = 'rs-body rs-gg__card-quote';
      p.textContent = data.body;
      quoteEl.innerHTML = '';
      quoteEl.appendChild(p);
    }

    const nameEl = skel.querySelector(NAME_ATTR);
    if (nameEl) {
      const replacement = document.createElement('p');
      replacement.className = 'rs-label rs-gg__card-name';
      replacement.textContent = data.author || '';
      nameEl.parentNode.replaceChild(replacement, nameEl);
    }

    const metaEl = skel.querySelector(META_ATTR);
    if (metaEl) {
      const replacement = document.createElement('p');
      replacement.className = 'rs-caption rs-gg__card-meta';
      replacement.textContent = data.verified ? 'Verified buyer' : '';
      metaEl.parentNode.replaceChild(replacement, metaEl);
    }

    // Product tag (suppressed on shop-level reviews; defensive on missing data)
    const tagEl = skel.querySelector('[data-rs-gg-product-tag]');
    if (tagEl) {
      if (!data.isShopReview && data.productTitle && data.productUrl) {
        tagEl.textContent = data.productTitle;
        tagEl.setAttribute('href', data.productUrl);
        tagEl.removeAttribute('hidden');
      } else {
        // Shop reviews / missing data: remove the anchor entirely rather than
        // hide via [hidden]. (The tag composed `.rs-eyebrow`, display:
        // block !important, until Digital #99; it is `.rs-caption` now, but
        // removing the element still keeps the a11y tree accurate.)
        tagEl.remove();
      }
    }

    skel.classList.remove('is-loading');
    skel.classList.add('is-filled');
    skel.setAttribute('aria-busy', 'false');
    return true;
  }

  function hideAll() {
    document.querySelectorAll(SKELETON_SEL).forEach(function (s) {
      s.classList.remove('is-loading');
      s.classList.add('is-empty');
    });
  }

  // ── Hydrate skeletons from JSON all_reviews ──────────────────────────
  function hydrate() {
    const skeletons = document.querySelectorAll(SKELETON_SEL);
    if (skeletons.length === 0) return;

    const data = readJMData();
    const reviews = (data && Array.isArray(data.all_reviews)) ? data.all_reviews : [];

    if (reviews.length === 0) {
      hideAll();
      return;
    }

    skeletons.forEach(function (skel, i) {
      const review = reviews[i];
      if (!review) {
        skel.classList.remove('is-loading');
        skel.classList.add('is-empty');
        return;
      }
      const fillData = recordToData(review);
      const filled = fillSkeleton(skel, fillData);
      if (!filled) {
        skel.classList.remove('is-loading');
        skel.classList.add('is-empty');
      }
    });
  }

  function init() {
    if (!document.querySelector(SECTION_SEL)) return;
    if (!document.querySelector(SKELETON_SEL)) return;
    hydrate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
